import { afterRenderEffect, ChangeDetectionStrategy, Component, computed, effect, ElementRef, OnDestroy, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FloChatService } from '../../../services/flo-chat.service';
import { FeedbackRating, FloMessage } from '../../../model/flo';
import { FloMessageComponent } from './flo-message.component';
import { FloHistoryComponent } from './flo-history.component';
import { daySeparatorLabel, sameDay } from './flo-time.util';

/** A rendered chat row: the message plus the day-divider label that precedes it (null = none). */
interface ChatRow {
	daySeparator: string | null;
	message: FloMessage;
}

/**
 * Shown while Flô works before the backend reports a progress label. The only client-side
 * progress copy: backend labels are rendered verbatim. No ellipsis — the indicator animates its own dots.
 */
export const FLO_PROGRESS_FALLBACK_LABEL = 'Pensando';

@Component({
	selector: 'app-flo-panel',
	standalone: true,
	imports: [FormsModule, FloMessageComponent, FloHistoryComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
	templateUrl: './flo-panel.component.html',
	styleUrls: ['./flo-panel.component.scss']
})
export class FloPanelComponent implements OnDestroy {
	/** The unsent message text, backed by the service so it survives minimize/restore (the panel is
	 *  destroyed on collapse). The template binds `[(ngModel)]="draft"` to this getter/setter. */
	get draft(): string { return this.flo.draft(); }
	set draft(value: string) { this.flo.draft.set(value); }
	/** Max chars for a single message — mirrors the server guard (FloChatController.MAX_MESSAGE_CHARS). */
	readonly maxChars = 2000;
	/** Characters from the limit at which the counter becomes visible. */
	readonly counterShowThreshold = 200;
	/** Characters from the limit at which the counter enters the warning state. */
	readonly counterWarnThreshold = 20;
	@ViewChild('scrollArea') private scrollArea?: ElementRef<HTMLElement>;
	@ViewChild('inputEl') private inputEl?: ElementRef<HTMLInputElement>;

	/**
	 * Visible messages (the empty streaming placeholder is dropped — typing dots stand in for it),
	 * each tagged with a day-divider label when it opens a new calendar day.
	 */
	readonly rows = computed<ChatRow[]>(() => {
		const visible = this.flo.messages().filter(m => !(m.streaming && !m.content));
		return visible.map((message, i) => {
			const prev = visible[i - 1];
			const daySeparator = (!prev || !sameDay(prev.createdAt, message.createdAt))
				? (daySeparatorLabel(message.createdAt) || null)
				: null;
			return { daySeparator, message };
		});
	});

	/**
	 * Milliseconds since the current "working" phase began (busy with no streamed content yet).
	 * Drives {@link workingLabel}; ticked by an interval only while that phase is active.
	 */
	readonly elapsedMs = signal(0);
	private workingSince = 0;
	private ticker: ReturnType<typeof setInterval> | null = null;

	/** True while an older page is being prepended — suppresses the pin-to-bottom effect so the
	 *  viewport stays anchored instead of jumping to the newest message. */
	private prepending = false;
	private static readonly LOAD_OLDER_THRESHOLD_PX = 80;
	private static readonly TICKER_INTERVAL_MS = 500;
	private static readonly WORKING_STAGE2_S = 4;
	private static readonly WORKING_STAGE3_S = 12;

	/** Backend progress label of the in-flight turn (null until one arrives, or once content streams). */
	readonly progressLabel = computed<string | null>(() => {
		const msgs = this.flo.messages();
		const last = msgs[msgs.length - 1];
		return last && last.role === 'assistant' && last.streaming && !last.content ? (last.progressLabel ?? null) : null;
	});

	/**
	 * Text shown while Flô works before the first token streams. Precedence: the backend's progress
	 * label (verbatim, truth beats time guesses) -> elapsed-time wording for older backends that
	 * emit no progress frames. Stage 1 of the time wording is {@link FLO_PROGRESS_FALLBACK_LABEL},
	 * the only client-side copy. No trailing ellipsis — the animated dots already convey progress.
	 */
	readonly workingLabel = computed(() => {
		const backendLabel = this.progressLabel();
		if (backendLabel !== null) return backendLabel;
		const seconds = this.elapsedMs() / 1000;
		if (seconds < FloPanelComponent.WORKING_STAGE2_S) return FLO_PROGRESS_FALLBACK_LABEL;
		if (seconds < FloPanelComponent.WORKING_STAGE3_S) return 'Analisando as fontes e os dados do relatório';
		return 'Quase lá — organizando a resposta';
	});

	constructor(public flo: FloChatService) {
		// Move focus to the input after the panel mounts (two rAF so layout is settled).
		// Mirrors the scrollToEnd pattern; guarded for SSR.
		if (typeof requestAnimationFrame !== 'undefined') {
			requestAnimationFrame(() => requestAnimationFrame(() => {
				this.inputEl?.nativeElement?.focus();
			}));
		}

		// Keep the conversation pinned to the newest message after each render cycle.
		// afterRenderEffect is browser-only by design, so no SSR guard needed.
		// The {write} phase runs post-layout; a single inner rAF is enough for markdown
		// bubbles that finish painting after the Angular CD pass.
		afterRenderEffect({
			write: () => {
				// Read signals inside the write callback — Angular tracks them as dependencies.
				this.flo.messages();
				this.flo.threadLoading();
				if (!this.prepending) {
					const el = this.scrollArea?.nativeElement;
					if (el) requestAnimationFrame(() => el.scrollTop = el.scrollHeight);
				}
			}
		});
		// Run the working-label ticker only during the pre-first-token phase (busy + no content yet).
		effect(() => {
			if (this.flo.busy() && !this.lastHasContent()) {
				this.startTicker();
			} else {
				this.stopTicker();
			}
		});
	}

	/** Inline error shown under the conversation when the report PDF could not be generated. */
	readonly reportError = signal<string | null>(null);
	private static readonly REPORT_ERROR_MS = 6000;
	private reportErrorTimer: ReturnType<typeof setTimeout> | null = null;

	/** Opens the report PDF. The in-progress lock lives in the service (it survives the panel being
	 *  recreated), so clicks while a download is being prepared are ignored. */
	async downloadReport(): Promise<void> {
		if (this.flo.reportDownloading()) return;
		this.reportError.set(null);
		const ok = await this.flo.downloadReportPdf();
		if (!ok) {
			this.showReportError(this.flo.reportFailure() ?? 'Não foi possível gerar o relatório. Tente novamente.');
		}
	}

	private showReportError(message: string): void {
		this.reportError.set(message);
		if (this.reportErrorTimer) clearTimeout(this.reportErrorTimer);
		this.reportErrorTimer = setTimeout(() => this.reportError.set(null), FloPanelComponent.REPORT_ERROR_MS);
	}

	/** Clear the ticker if the panel is destroyed mid-work (e.g. navigating away from the report
	 *  while Flô is still thinking) — the busy→false effect won't fire on a torn-down component. */
	ngOnDestroy(): void {
		this.stopTicker();
		if (this.reportErrorTimer) clearTimeout(this.reportErrorTimer);
	}

	/** Starts the elapsed-time ticker (idempotent; browser-only — no-op under SSR). */
	private startTicker(): void {
		if (this.ticker !== null || typeof requestAnimationFrame === 'undefined') return;
		this.workingSince = Date.now();
		this.elapsedMs.set(0);
		this.ticker = setInterval(() => this.elapsedMs.set(Date.now() - this.workingSince), FloPanelComponent.TICKER_INTERVAL_MS);
	}

	/** Stops the ticker and resets the elapsed counter so the next turn starts fresh. */
	private stopTicker(): void {
		if (this.ticker !== null) {
			clearInterval(this.ticker);
			this.ticker = null;
		}
		if (this.elapsedMs() !== 0) this.elapsedMs.set(0);
	}

	// scrollToEnd() removed — afterRenderEffect({ write }) handles post-render scroll pinning.

	/** Auto-loads older messages when the user scrolls near the top, anchoring the scroll position
	 *  so the prepended page doesn't shove the viewport. Browser-only (no scroll under SSR). */
	onScroll(): void {
		const el = this.scrollArea?.nativeElement;
		if (!el || this.prepending || !this.flo.hasMoreMessages()) return;
		if (el.scrollTop > FloPanelComponent.LOAD_OLDER_THRESHOLD_PX) return;
		this.prepending = true;
		const prevHeight = el.scrollHeight;
		void this.flo.loadOlderMessages().then(count => {
			if (typeof requestAnimationFrame === 'undefined') { this.prepending = false; return; }
			requestAnimationFrame(() => requestAnimationFrame(() => {
				const node = this.scrollArea?.nativeElement;
				if (node && count > 0) node.scrollTop += node.scrollHeight - prevHeight;
				this.prepending = false;
			}));
		});
	}

	toggleHistory(): void {
		this.flo.historyOpen.set(!this.flo.historyOpen());
		if (this.flo.historyOpen()) this.flo.loadThreads();
	}

	readonly conversationStarted = computed<boolean>(() =>
		this.flo.messages().some(m => m.role === 'user'));

	readonly lastHasContent = computed<boolean>(() => {
		const m = this.flo.messages();
		return m.length > 0 && m[m.length - 1].content.length > 0;
	});

	submit(): void {
		const text = this.draft.trim();
		if (!text || this.flo.busy()) return;
		this.draft = '';
		void this.flo.sendMessage(text);
	}

	sendPill(text: string): void { void this.flo.sendMessage(text); }

	/** Downloads the open conversation as a branded PDF (no-op if there's no persisted thread yet). */
	onExport(): void {
		const threadId = this.flo.threadId();
		if (threadId) {
			this.reportError.set(null);
			void this.flo.exportPdf(threadId);
		}
	}

	/** Relays a message's thumbs rating to the service (null rating = the user un-toggled it). */
	onFeedback(messageId: string, e: { rating: FeedbackRating | null; comment?: string }): void {
		if (e.rating === null) this.flo.clearFeedback(messageId);
		else this.flo.sendFeedback(messageId, e.rating, e.comment);
	}
}
